import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { FaArrowLeft, FaArrowRight, FaMinus, FaPlus, FaTimes, FaUndo } from "react-icons/fa";

const fallbackNewsClippings = [
  {
    id: "fallback-1",
    src: "/images/image-fallback.svg",
    alt: "News clipping",
    heading: "Local Daily Coverage - Para Meet",
    date: new Date().toISOString()
  }
];

const toDateKey = (value) => {
  if (!value) return "unknown-date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown-date";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isSameLocalDate = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDateHeading = (dateKey) => {
  if (dateKey === "unknown-date") return "Unknown date";
  const [year, month, day] = dateKey.split("-").map(Number);
  const sectionDate = new Date(year, month - 1, day);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameLocalDate(sectionDate, today)) return "Today";
  if (isSameLocalDate(sectionDate, yesterday)) return "Yesterday";

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(sectionDate);
};

const formatDateSubheading = (dateKey) => {
  if (dateKey === "unknown-date") return "";
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(year, month - 1, day));
};

const formatNewsDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const News = () => {
  const [clips, setClips] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 4;
  const ZOOM_STEP = 0.25;

  useEffect(() => {
    let mounted = true;
    const loadNewsClips = async () => {
      try {
        const data = await API.fetchNewsClips();
        if (!mounted) return;
        setClips(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!mounted) return;
        setClips([]);
      }
    };
    loadNewsClips();
    return () => {
      mounted = false;
    };
  }, []);

  // Flatten and map news images to look exactly like gallery photos list
  const newsPhotos = useMemo(() => {
    if (!clips.length) return fallbackNewsClippings;
    const list = [];
    clips.forEach((clip) => {
      const paths = Array.isArray(clip.image_paths) && clip.image_paths.length > 0 
        ? clip.image_paths 
        : (clip.image_path ? [clip.image_path] : []);

      paths.forEach((path, idx) => {
        list.push({
          id: `${clip.id}-${idx}`,
          src: resolveAssetUrl(path, { folder: "news-clips" }),
          heading: clip.heading,
          date: clip.date,
        });
      });
    });

    // Sort by date (newest first)
    return list.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  }, [clips]);

  const selectedIndex = useMemo(
    () => (selectedImage ? newsPhotos.findIndex((img) => img.id === selectedImage.id) : -1),
    [newsPhotos, selectedImage]
  );

  const groupedClipsByDate = useMemo(() => {
    return clips.reduce((acc, clip) => {
      const dateKey = toDateKey(clip.date);
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(clip);
      return acc;
    }, {});
  }, [clips]);

  const sortedNewsDateKeys = useMemo(() => {
    return Object.keys(groupedClipsByDate).sort((a, b) => {
      if (a === "unknown-date") return 1;
      if (b === "unknown-date") return -1;
      return b.localeCompare(a); // Sort descending (newest dates first)
    });
  }, [groupedClipsByDate]);

  const openImage = (img) => {
    setSelectedImage(img);
    setZoom(1);
  };

  const closeImage = () => {
    setSelectedImage(null);
    setZoom(1);
  };

  const clampZoom = (value) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
  const increaseZoom = () => setZoom((prev) => clampZoom(prev + ZOOM_STEP));
  const decreaseZoom = () => setZoom((prev) => clampZoom(prev - ZOOM_STEP));
  const resetZoom = () => setZoom(1);

  const goPrev = (e) => {
    e?.stopPropagation();
    if (selectedIndex === -1 || newsPhotos.length === 0) return;
    const prevIndex = (selectedIndex - 1 + newsPhotos.length) % newsPhotos.length;
    setSelectedImage(newsPhotos[prevIndex]);
    setZoom(1);
  };

  const goNext = (e) => {
    e?.stopPropagation();
    if (selectedIndex === -1 || newsPhotos.length === 0) return;
    const nextIndex = (selectedIndex + 1) % newsPhotos.length;
    setSelectedImage(newsPhotos[nextIndex]);
    setZoom(1);
  };

  // Lock scroll when modal is open
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => (document.body.style.overflow = "auto");
  }, [selectedImage]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!selectedImage) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeImage();
        return;
      }
      if (event.key === "ArrowLeft") goPrev(event);
      else if (event.key === "ArrowRight") goNext(event);
      else if (event.key === "+" || event.key === "=") increaseZoom();
      else if (event.key === "-" || event.key === "_") decreaseZoom();
      else if (event.key === "0") resetZoom();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage, selectedIndex, newsPhotos.length]);

  return (
    <>
      <Navbar />

      <main className="bg-slate-50 min-h-screen">
        {/* ===== HERO SECTION ===== */}
        <section className="bg-white py-16 text-center border-b border-slate-200">
          <div className="max-w-screen-lg mx-auto px-6">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">In the Media</h1>
            <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">
              See how the journey and achievements of our athletes are being covered in the news.
            </p>
          </div>
        </section>

        {/* ===== NEWS CLIPS GRID ===== */}
        <section className="py-12 px-4">
          <div className="max-w-screen-xl mx-auto">
            {sortedNewsDateKeys.length === 0 ? (
              <div className="text-center bg-white p-12 rounded-xl shadow-md text-slate-500">
                No news clips have been uploaded yet.
              </div>
            ) : (
              <div className="space-y-12">
                {sortedNewsDateKeys.map((dateKey) => (
                  <div key={dateKey} className="space-y-8">
                    {/* Sticky Date Header block */}
                    <div className="sticky top-[64px] z-10 bg-slate-50/95 py-2.5 border-b border-slate-200 backdrop-blur flex items-end justify-between">
                      <div>
                        <h3 className="text-base font-bold text-slate-900">
                          {formatDateHeading(dateKey)}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {formatDateSubheading(dateKey)}
                        </p>
                      </div>
                    </div>

                    {/* Render each news clip under this date separately */}
                    {groupedClipsByDate[dateKey].map((clip) => {
                      const paths = Array.isArray(clip.image_paths) && clip.image_paths.length > 0 
                        ? clip.image_paths 
                        : (clip.image_path ? [clip.image_path] : []);

                      return (
                        <div key={clip.id} className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
                          {/* Heading Accent */}
                          <h4 className="text-lg font-bold text-slate-900 mb-4 border-l-4 border-blue-600 pl-3">
                            {clip.heading}
                          </h4>

                          {/* Photo Collage Grid for this news clip */}
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                            {paths.map((path, idx) => {
                              const photoId = `${clip.id}-${idx}`;
                              const photoSrc = resolveAssetUrl(path, { folder: "news-clips" });
                              // Find matching photo object in newsPhotos flat list for lightbox slideshow
                              const globalIdx = newsPhotos.findIndex((p) => p.id === photoId);
                              const flatPhoto = newsPhotos[globalIdx] || {
                                id: photoId,
                                src: photoSrc,
                                heading: clip.heading,
                                date: clip.date,
                              };

                              return (
                                <button
                                  key={photoId}
                                  type="button"
                                  onClick={() => openImage(flatPhoto)}
                                  className="relative aspect-video block w-full overflow-hidden rounded-xl shadow-md group transform transition-transform duration-300 ease-in-out hover:scale-105 border border-slate-200 bg-white"
                                >
                                  <img
                                    src={photoSrc}
                                    alt={clip.heading}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                  <div className="absolute inset-0 p-4 bg-gradient-to-t from-black/85 via-black/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end text-left">
                                    <p className="text-[11px] font-medium text-slate-300 flex items-center gap-1">
                                      <span>📅</span> {formatNewsDate(clip.date)}
                                    </p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      
      {/* ===== LIGHTBOX MODAL ===== */}
      {selectedImage && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md animate-fade-in"
          onClick={closeImage}
        >
          <button
            type="button"
            onClick={closeImage}
            aria-label="Close image viewer"
            className="absolute top-4 right-4 z-10 bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
          >
            <FaTimes size={24} />
          </button>

          <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={decreaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom out"
            >
              <FaMinus size={14} />
            </button>
            <button
              type="button"
              onClick={increaseZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Zoom in"
            >
              <FaPlus size={14} />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              className="bg-white/20 text-white rounded-full p-2 hover:bg-white/30 transition-colors"
              aria-label="Reset zoom"
            >
              <FaUndo size={14} />
            </button>
          </div>

          {/* Global slideshow navigation */}
          {newsPhotos.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous image"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors"
              >
                <FaArrowLeft size={24} />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next image"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/20 text-white rounded-full p-3 hover:bg-white/30 transition-colors"
              >
                <FaArrowRight size={24} />
              </button>
            </>
          )}

          <div
            className="relative max-w-5xl w-full max-h-[92vh] p-2 sm:p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rounded-xl overflow-hidden bg-black/80">
              <div
                className="flex items-center justify-center p-2 sm:p-3 bg-black overflow-auto"
                onWheel={(event) => {
                  event.preventDefault();
                  if (event.deltaY < 0) {
                    increaseZoom();
                  } else {
                    decreaseZoom();
                  }
                }}
              >
                <img
                  src={selectedImage.src}
                  alt={selectedImage.heading}
                  className="max-h-[78vh] w-auto max-w-full object-contain"
                  style={{
                    transform: `scale(${zoom})`,
                    transition: "transform 120ms ease-out",
                  }}
                />
              </div>
              <div className="px-5 py-4 text-center bg-slate-900/95 border-t border-white/10">
                <p className="text-white text-lg font-bold tracking-tight">
                  {selectedImage.heading}
                </p>
                <p className="text-slate-400 text-xs mt-1.5 flex items-center justify-center gap-1">
                  <span>📅</span> {formatNewsDate(selectedImage.date)}
                  {newsPhotos.length > 1 && (
                    <span className="ml-2 bg-slate-800 text-white px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                      {selectedIndex + 1} of {newsPhotos.length}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <style>{`
        @keyframes fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes fade-out {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }
        .animate-fade-out {
          animation: fade-out 0.22s ease-in forwards;
        }
      `}</style>
    </>
  );
};

export default News;

